import { useEffect, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Icon } from "../lib/Icon";
import { usePortal } from "./state";
import { search as runSearch } from "./reading";
import { ArticleCard, SectionHeader } from "./components";

export function PortalAsk() {
  const { brand, brandId, spaceId, user } = usePortal();
  const navigate = useNavigate();
  const initial = (useSearch({ strict: false }) as { q?: string }).q ?? "";
  const [q, setQ] = useState(initial);
  const [answered, setAnswered] = useState<null | boolean>(null);
  // Resync the box when ?q= changes (header search, back/forward), so the input
  // never shows a stale query while the results below have moved on.
  useEffect(() => {
    setQ(initial);
    setAnswered(null);
  }, [initial]);

  const { results, answer } = runSearch(brandId, spaceId, brand, "mark", user.org, initial);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    navigate({ to: "/portal/ask", search: q.trim() ? { q: q.trim() } : {} });
  }

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "30px 28px 64px", display: "flex", flexDirection: "column", gap: 20 }}>
      <form onSubmit={submit} style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.1)", padding: "12px 14px" }}>
          <Icon name="search" size={18} color={brand.ink} />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask a question or search" style={{ flex: 1, border: 0, outline: 0, fontSize: 14.5, color: "#1d241f", fontFamily: "inherit" }} />
        </div>
        <button type="submit" style={{ border: 0, background: brand.ink, color: "#fff", borderRadius: 3, padding: "0 22px", fontSize: 12, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase", cursor: "pointer" }}>Ask</button>
      </form>

      {/* AI answer — bounded by its sources, always cited (DECISIONS §7) */}
      {answer && initial && (
        <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.08)", padding: "18px 18px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <Icon name="sparkles" size={16} color={brand.ink} />
            <SectionHeader>Answer</SectionHeader>
            <span style={{ flex: 1 }} />
            {answer.confidence > 0 && <span style={{ fontSize: 10.5, fontWeight: 600, color: "#8a8e88" }}>{answer.confidence}% confidence</span>}
          </div>
          <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.65, color: "#2d332e", textWrap: "pretty" }}>{answer.text}</p>
          {answer.sources.length > 0 && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #f4f3ee" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#8a8e88", marginBottom: 7 }}>Drawn from</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {answer.sources.map(([sid, title]) => (
                  <Link key={sid} to="/portal/article/$id" params={{ id: sid }} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: brand.ink, textDecoration: "none" }}>
                    <Icon name="file-text" size={14} color={brand.ink} />
                    {title}
                  </Link>
                ))}
              </div>
            </div>
          )}
          <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 10 }}>
            {answered === null ? (
              <>
                <span style={{ fontSize: 12.5, color: "#8a8e88" }}>Did this answer it?</span>
                <button onClick={() => setAnswered(true)} style={{ border: "1px solid #dcdad2", background: "#fff", borderRadius: 3, padding: "5px 13px", fontSize: 12.5, fontWeight: 600, color: "#4a504a", cursor: "pointer" }}>Yes</button>
                <button onClick={() => setAnswered(false)} style={{ border: "1px solid #dcdad2", background: "#fff", borderRadius: 3, padding: "5px 13px", fontSize: 12.5, fontWeight: 600, color: "#4a504a", cursor: "pointer" }}>No</button>
              </>
            ) : (
              <span style={{ fontSize: 12.5, color: "#4a504a", display: "flex", alignItems: "center", gap: 7 }}>
                <Icon name="check-circle" size={15} color="#1f8a5b" />
                {answered ? "Thanks — glad it helped." : "Thanks — a ticket may get you a faster answer."}
              </span>
            )}
            <span style={{ flex: 1 }} />
            <Link to="/portal/new-ticket" style={{ fontSize: 12.5, color: brand.ink, textDecoration: "none" }}>Open a ticket instead →</Link>
          </div>
        </div>
      )}

      <div>
        <div style={{ marginBottom: 12 }}>
          <SectionHeader>{initial ? `Results in ${brand.name}` : "Start typing to search"}</SectionHeader>
        </div>
        {results.length === 0 ? (
          <div style={{ border: "1px dashed #dcdad2", background: "#fafaf7", borderRadius: 3, padding: "34px 20px", textAlign: "center" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#1d241f" }}>Nothing matched in this space</div>
            <p style={{ fontSize: 13, color: "#8a8e88", margin: "8px 0 0" }}>Try different words, or <Link to="/portal/new-ticket" style={{ color: brand.ink }}>open a ticket</Link> and a person will answer.</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {results.map((c) => (
              <ArticleCard key={c.id} card={c} brand={brand} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
